import * as babylon from "@babylonjs/core";

import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services";
import { TopicData } from "@/shared/state/visualization/schema/v1/topic-data";

import {
  messagePathKey,
  TopicIdToMessagePaths,
  TopicIdToMessagePathValues,
} from "../../../DataServer/messaging";
import * as components from "../../components";
import { BabylonContext, BabylonState } from "../../state";

import { projectLLAToENU } from "./projectPositions";

/**
 * Checks for stale positions, and loads their caches if needed.
 */
export function reloadStalePositions(state: BabylonState) {
  for (const entity of Object.values(state.entities)) {
    if (!entity.position || entity.position.cache.load.state !== "stale") {
      continue;
    }
    // Make sure the stale data load is aborted
    entity.position.cache.load.abortController.abort();

    // Start the new data load.
    entity.position.cache.load.state = "loading";
    const abortController = new AbortController();
    entity.position.cache.load.abortController = abortController;
    entity.position.cache.allPoints = [];
    reloadCache(entity.position, state.context, abortController.signal).catch(
      (e) => {
        if (isAbortError(e) && abortController.signal.aborted) {
          return;
        }
        ErrorMonitoringService.captureError(e);
      },
    );
  }
}

/**
 * Loads the given position's cache.
 *
 * This operates only on the given position component instead of the full state.
 * This helps avoid conflicting states if the position is overwritten with a new one
 * during an async gap.
 */
async function reloadCache(
  position: components.Position,
  context: BabylonContext,
  signal: AbortSignal,
) {
  const { right, up, forward, rightScale, upScale, forwardScale } =
    position.spec;
  const toRead = [right, up, forward];
  // If there are no message paths on this position to read, we're done.
  if (
    typeof right === "number" &&
    typeof forward === "number" &&
    typeof up === "number"
  ) {
    const vector = new babylon.Vector3(
      right * position.spec.rightScale,
      up * position.spec.upScale,
      forward * position.spec.forwardScale,
    );
    position.current = { logtime: 0n, vector };
    position.mean = vector;
    position.cache.logtimes = [0n];
    position.cache.allPoints = [vector];
    position.cache.load.state = "loaded";
    return;
  }

  const topicIdToMessagePaths: TopicIdToMessagePaths = {};
  for (const topicData of Object.values(toRead)) {
    if (typeof topicData === "number") {
      continue;
    }
    const mp = {
      id: topicData.messagePath.id,
      parts: topicData.messagePath.parts,
    };
    topicIdToMessagePaths[topicData.topic.id] ??= {};
    topicIdToMessagePaths[topicData.topic.id][messagePathKey(mp)] = mp;
  }

  const topicIdToMessagePathValues = await context.dataServer.getAllData(
    topicIdToMessagePaths,
    signal,
  );
  signal.throwIfAborted();
  // Stitch together x,y, and z coordinates that may
  // come from distinct topics with distinct time bounds.
  // We'll construct one vector for each unique log time
  const points: babylon.Vector3[] = [];
  const logtimes: bigint[] = [];
  // Assumption: extractValues will return parallel arrays with at least one element.
  const xs = extractValues(topicIdToMessagePathValues, right, rightScale);
  const ys = extractValues(topicIdToMessagePathValues, up, upScale);
  const zs = extractValues(topicIdToMessagePathValues, forward, forwardScale);

  let xi = 0;
  let yi = 0;
  let zi = 0;
  do {
    const t = bigIntMax(xs.logtimes[xi], ys.logtimes[yi], zs.logtimes[zi]);
    const vector = new babylon.Vector3(
      xs.values[xi],
      ys.values[yi],
      zs.values[zi],
    );
    logtimes.push(t);
    points.push(vector);

    // Find the next logtime and increment all indices as appropriate.
    const nextXTime = xs.logtimes.at(xi + 1);
    const nextYTime = ys.logtimes.at(yi + 1);
    const nextZTime = zs.logtimes.at(zi + 1);
    const nextTime = bigIntMin(nextXTime, nextYTime, nextZTime);
    if (nextXTime === nextTime && xi < xs.logtimes.length - 1) {
      xi++;
    }
    if (nextYTime === nextTime && yi < ys.logtimes.length - 1) {
      yi++;
    }
    if (nextZTime === nextTime && zi < zs.logtimes.length - 1) {
      zi++;
    }
  } while (
    // Once all indices have reached the end of their arrays, we're done.
    xi < xs.logtimes.length - 1 ||
    yi < ys.logtimes.length - 1 ||
    zi < zs.logtimes.length - 1
  );

  if (position.spec.transformation === "lla_to_enu") {
    const llaOrigin = points[0];
    for (let i = 0; i < points.length; i++) {
      points[i] = projectLLAToENU(points[i], llaOrigin);
    }
  }

  position.current = { logtime: logtimes[0], vector: points[0] };
  position.mean = babylon.Vector3.Zero();
  const divisor = new babylon.Vector3(
    points.length,
    points.length,
    points.length,
  );
  for (const p of points) {
    position.mean.addInPlace(p);
  }
  position.mean.divideInPlace(divisor);
  position.cache.load.state = "loaded";
  position.cache.logtimes = logtimes;
  position.cache.allPoints = points;
}

function extractValues(
  topicIdToData: TopicIdToMessagePathValues,
  component: TopicData | number,
  scale: number,
): { logtimes: bigint[]; values: number[] } {
  if (typeof component === "number") {
    return { logtimes: [0n], values: [component * scale] };
  } else {
    const result = {
      logtimes: topicIdToData[component.topic.id].logtimes,
      values: topicIdToData[component.topic.id].values[
        messagePathKey(component.messagePath)
      ].map((v) => v * scale),
    };
    if (result.logtimes.length === 0) {
      return {
        logtimes: [0n],
        values: [NaN],
      };
    }
    return result;
  }
}

function bigIntMax(...args: bigint[]) {
  return args.reduce((max, current) => (current < max ? max : current));
}

function bigIntMin(...args: (bigint | undefined)[]) {
  return args.reduce((min, current) =>
    current === undefined || (min !== undefined && current > min)
      ? min
      : current,
  );
}
